// Б-5: эвристики picker'ов для бота.
// Простые статические правила выбора цели/жертвы из списка кандидатов.
// Тай-брейки случайные через rng — без стабильного порядка, чтобы не
// возникало детерминированных смещений между матчами.
//
// Все функции принимают массив кандидатов (НЕ state) и rng. Если массив
// пустой — возвращают null. Это позволяет легко тестировать в изоляции.

// Универсальный «макс по ключевой функции» с тай-брейком rng.
function pickMaxBy(items, scoreFn, rng) {
  if (!items || items.length === 0) return null;
  let bestScore = -Infinity;
  const tied = [];
  for (const it of items) {
    const s = scoreFn(it);
    if (s > bestScore) {
      bestScore = s;
      tied.length = 0;
      tied.push(it);
    } else if (s === bestScore) {
      tied.push(it);
    }
  }
  if (tied.length === 1) return tied[0];
  return tied[rng.int(tied.length)];
}

// Универсальный «мин по ключевой функции» с тай-брейком rng.
function pickMinBy(items, scoreFn, rng) {
  if (!items || items.length === 0) return null;
  return pickMaxBy(items, (it) => -scoreFn(it), rng);
}

function curAtk(c) { return c?.current?.atk ?? c?.base?.atk ?? 0; }
function curHp(c)  { return c?.current?.hp  ?? c?.base?.hp  ?? 0; }
function maxHp(c)  { return c?.base?.hp ?? 0; }

// Враг с наивысшей текущей атакой. Тай-брейк: наибольший current_hp
// (страшнее тот кто и бьёт и стоит дольше). Финальный тай-брейк rng.
export function chooseEnemyByHighestAtk(enemies, rng) {
  if (!enemies || enemies.length === 0) return null;
  // Сначала отбираем по atk.
  const byAtk = pickMaxBy(enemies, curAtk, rng);
  if (!byAtk) return null;
  const topAtk = curAtk(byAtk);
  const tied = enemies.filter(e => curAtk(e) === topAtk);
  if (tied.length === 1) return tied[0];
  // Среди равных по atk — самый «толстый» (current_hp).
  return pickMaxBy(tied, curHp, rng);
}

// Свой юнит с наивысшей текущей атакой (стандартный «бафф/защити сильного»).
// Тай-брейк rng.
export function chooseAllyByHighestAtk(allies, rng) {
  return pickMaxBy(allies || [], curAtk, rng);
}

// Свой юнит с наименьшим отношением current_hp/max_hp («самый раненый»).
// Полные юниты идут в конец. Тай-брейк rng.
export function chooseAllyByLowestHpRatio(allies, rng) {
  return pickMinBy(allies || [], (c) => {
    const m = maxHp(c) || 1;
    return curHp(c) / m;
  }, rng);
}

// Свой юнит с наивысшей атакой; среди равных предпочитаем РАНЕНОГО
// (current_hp < max_hp). Используется в «В карантин!» — лечим того,
// кому это пригодится.
export function chooseAllyByHighestAtkPreferDamaged(allies, rng) {
  if (!allies || allies.length === 0) return null;
  const damaged = allies.filter(c => curHp(c) < maxHp(c));
  if (damaged.length > 0) return chooseAllyByHighestAtk(damaged, rng);
  return chooseAllyByHighestAtk(allies, rng);
}

// Подсчёт «веса способностей» юнита: keywords + наличие текстовых способностей.
// Не различает passive/trigger/order/deathrattle/aura — для эвристики достаточно факта.
function abilityWeight(card) {
  let w = 0;
  if (Array.isArray(card?.keywords)) w += card.keywords.length;
  const t = card?.ability_text || {};
  if (t.passive)     w += 1;
  if (t.trigger)     w += 1;
  if (t.order)       w += 1;
  if (t.deathrattle) w += 1;
  if (t.aura)        w += 1;
  return w;
}

// Враг с наибольшим числом keywords/способностей (Молчание / Сапёр).
// Если все равны (никаких способностей нет) — null, чтобы caller взял random.
export function chooseEnemyByMostKeywords(enemies, rng) {
  if (!enemies || enemies.length === 0) return null;
  let maxW = -1;
  for (const e of enemies) {
    const w = abilityWeight(e);
    if (w > maxW) maxW = w;
  }
  if (maxW <= 0) return null;
  const tied = enemies.filter(e => abilityWeight(e) === maxW);
  if (tied.length === 1) return tied[0];
  return tied[rng.int(tied.length)];
}

// Юнит из стопки сброса с наивысшей атакой (для Опытного хирурга).
// Только живые карты type='unit'. Тай-брейк rng.
export function chooseUnitByHighestAtkInDiscard(discard, rng) {
  if (!discard || discard.length === 0) return null;
  const units = discard.filter(c => c?.type === 'unit');
  return pickMaxBy(units, curAtk, rng);
}

// Multi-select для Медика-штурмовика: до maxCount своих юнитов с
// наименьшим current_hp. Если ВСЕ союзники на полном ХП — возвращаем
// пустой массив (отказ от жертвы).
export function chooseAlliesToSacrificeByLowestHp(allies, rng, maxCount = 2) {
  if (!allies || allies.length === 0) return [];
  // Только раненые.
  const damaged = allies.filter(c => curHp(c) < maxHp(c));
  if (damaged.length === 0) return [];
  // Сортируем по current_hp возрастающе. Тай-брейк через rng — если несколько
  // юнитов на одинаковом hp, перемешаем их между собой.
  const buckets = new Map();
  for (const c of damaged) {
    const k = curHp(c);
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(c);
  }
  const sortedKeys = [...buckets.keys()].sort((a, b) => a - b);
  const out = [];
  for (const k of sortedKeys) {
    const bucket = buckets.get(k);
    // shuffle bucket
    for (let i = bucket.length - 1; i > 0; i--) {
      const j = rng.int(i + 1);
      [bucket[i], bucket[j]] = [bucket[j], bucket[i]];
    }
    for (const c of bucket) {
      if (out.length >= maxCount) return out;
      out.push(c);
    }
  }
  return out;
}
